import React from 'react';
import {
  Select as MuiSelect,
  styled,
  MenuItem,
  FormLabel,
  FormControl,
} from '@mui/material';
import { KeyboardArrowDown as KeyboardArrowDownIcon } from '@mui/icons-material';

/**
 * Custom Select component with enhanced styling and consistent design.
 * 
 * Features:
 * - Consistent modern design across light/dark themes
 * - Custom styled dropdown menu with proper theming
 * - Proper label positioning and styling
 * - Disabled and error state handling
 * - Automatic MenuItem styling for consistent options
 * 
 * @example
 * // Basic select with options
 * <Select label="Category" value={category} onChange={handleCategoryChange}>
 *   <MenuItem value="option1">Option 1</MenuItem>
 *   <MenuItem value="option2">Option 2</MenuItem>
 * </Select>
 * 
 * // Select with error state
 * <Select 
 *   label="Department" 
 *   value={department} 
 *   onChange={handleDepartmentChange}
 *   error={!!departmentError}
 *   required
 * >
 *   <MenuItem value="it">IT</MenuItem>
 *   <MenuItem value="hr">HR</MenuItem>
 * </Select>
 * 
 * // Disabled select
 * <Select 
 *   label="Status" 
 *   value={status} 
 *   onChange={handleStatusChange}
 *   disabled
 * >
 *   <MenuItem value="active">Active</MenuItem>
 *   <MenuItem value="inactive">Inactive</MenuItem>
 * </Select>
 */

// Helper function to get background color based on theme and state
const getBackgroundColor = (theme, isHovered = false, isSelected = false) => {
  if (theme.palette.mode === 'dark') {
    if (isSelected) {
      return isHovered ? 'rgba(255, 255, 255, 0.16)' : 'rgba(255, 255, 255, 0.12)';
    }
    return isHovered ? 'rgba(255, 255, 255, 0.05)' : 'transparent';
  } else {
    if (isSelected) {
      return isHovered ? 'rgba(0, 0, 0, 0.08)' : 'rgba(0, 0, 0, 0.06)';
    }
    return isHovered ? 'rgba(0, 0, 0, 0.02)' : 'transparent';
  }
};

// Helper function to get dropdown menu styles
const getMenuStyles = (theme) => ({
  mt: 1,
  borderRadius: '12px',
  border: '1px solid',
  borderColor: theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.1)' 
    : 'rgba(0, 0, 0, 0.1)',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 4px 20px rgba(0, 0, 0, 0.5)'
    : '0 4px 20px rgba(0, 0, 0, 0.15)',
  '& .MuiList-root': {
    padding: '6px',
  },
});

const StyledSelect = styled(MuiSelect)(({ theme }) => ({
  height: '48px',
  borderRadius: '12px',
  backgroundColor: getBackgroundColor(theme),
  transition: theme.transitions.create([
    'border-color',
    'background-color',
    'box-shadow',
  ]),

  '&:hover': {
    backgroundColor: getBackgroundColor(theme, true),
  },

  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.12)'
      : 'rgba(0, 0, 0, 0.12)',
    borderWidth: '1px',
    top: 0,
    legend: {
      display: 'none',
    },
  },

  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
  },

  // Стилизация иконки стрелки
  '& .MuiSelect-icon': {
    fontSize: 20,
    color: theme.palette.text.secondary,
    transition: 'transform 0.2s',
    right: 16,
  },

  '&.Mui-focused': {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
      borderWidth: '1px',
    },
  },

  // Disabled state
  '&.Mui-disabled': {
    backgroundColor: getBackgroundColor(theme),
    opacity: 0.7,
  },

  // Error state
  '&.Mui-error': {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.error.main,
    },
  },
}));

// Styled label to match TextField
const StyledFormLabel = styled(FormLabel)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  transform: 'none',
  position: 'relative',
  marginBottom: theme.spacing(1),
  '&.Mui-focused': {
    color: theme.palette.text.secondary,
  },
  '&.Mui-error': {
    color: theme.palette.error.main,
  },
}));

// Styled MenuItem for consistent dropdown options
const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  height: '44px',
  fontSize: '0.875rem',
  padding: '0 16px',
  display: 'flex',
  alignItems: 'center',
  transition: theme.transitions.create(['background-color']),
  borderRadius: '8px',
  margin: '2px 0',

  '&:hover': {
    backgroundColor: getBackgroundColor(theme, true),
  },

  '&.Mui-selected': {
    backgroundColor: getBackgroundColor(theme, false, true),
    '&:hover': {
      backgroundColor: getBackgroundColor(theme, true, true),
    },
  },

  '&.Mui-disabled': {
    opacity: 0.5,
  },
}));

/**
 * Enhanced Select component with custom styling and consistent design.
 * 
 * @param {Object} props - Component props
 * @param {string} props.label - Field label
 * @param {React.ReactNode} props.children - Select options (MenuItem components)
 * @param {boolean} props.error - Whether to show error state
 * @param {boolean} props.required - Whether the field is required
 * @param {any} props.value - Selected value
 * @param {Function} props.onChange - Change handler function
 * @param {boolean} props.disabled - Whether the select is disabled
 * @param {string} props.name - Input name attribute
 * @param {Object} props - Additional props passed to MUI Select
 * @param {React.Ref} ref - Forwarded ref
 */
const Select = React.forwardRef(
  ({ label, children, error, required, ...props }, ref) => {
    return (
      <FormControl fullWidth error={error}>
        {label && (
          <StyledFormLabel required={required}>{label}</StyledFormLabel>
        )}
        <StyledSelect
          variant="outlined"
          {...props}
          ref={ref}
          IconComponent={KeyboardArrowDownIcon}
          MenuProps={{
            PaperProps: {
              sx: getMenuStyles,
            },
          }}
        >
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child) && child.type === MenuItem) {
              return <StyledMenuItem {...child.props} />;
            }
            return child;
          })}
        </StyledSelect>
      </FormControl>
    );
  }
);

Select.displayName = 'Select';

export default Select;
